import { Button } from '@/components/Button';
import { twMerge } from 'tailwind-merge';
import luciusLogoUrl from '@/assets/svg/lucius-logo-white.svg';
import { Checkbox } from '@/components/Controls/Checkbox';
import { DragEvent } from 'react';

interface EntityTagProps {
  text: string;
  selected?: boolean;
  displayLogo?: boolean;
  logo?: string;
  children?: React.ReactNode;
  onSelect?: () => void;
  onSelectDisabled?: boolean;
  onEdit?: () => void;
  onEditDisabled?: boolean;
  onDelete?: () => void;
  onDeleteDisabled?: boolean;
  draggable?: boolean;
  onDragStart?: (event: React.DragEvent<HTMLElement>) => void;
  onDragOver?: (event: DragEvent<HTMLElement>) => void;
  onDragLeave?: (event: React.DragEvent<HTMLElement>) => void;
  onDrop?: (event: React.DragEvent<HTMLElement>) => void;
  textClassName?: string;
  containerClassName?: string;
}

export const EntityTag = ({
  text,
  selected,
  logo,
  displayLogo = true,
  children,
  onSelect,
  onSelectDisabled,
  onEdit,
  onEditDisabled,
  onDelete,
  onDeleteDisabled,
  draggable,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  containerClassName,
  textClassName,
}: EntityTagProps) => {
  return (
    <div
      className={twMerge(
        'flex flex-wrap items-center gap-2.5 py-2.5 pl-3.5 pr-2 border border-gray-300 rounded-lg overflow-hidden',
        !displayLogo && 'py-3.5',
        onSelect && 'hover:cursor-pointer',
        selected && 'bg-blue-131025',
        containerClassName
      )}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onClick={onSelect}
    >
      {draggable && (
        <div className="inline-flex justify-center items-center">
          <i className="ki-outline ki-arrow-two-diagonals text-md" />
        </div>
      )}
      {onSelect && (
        <div
          className="flex justify-center items-center rounded-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            text=""
            checked={selected}
            disabled={onSelectDisabled}
            onChange={onSelect}
          />
        </div>
      )}
      {displayLogo && (
        <div
          className={twMerge(
            'w-8 h-8 border border-blue-131025 bg-blue-131025 overflow-hidden rounded-full',
            selected && 'border-gray-500'
          )}
        >
          <img
            src={logo ?? luciusLogoUrl}
            alt="logo"
            className="w-full h-full object-cover object-center"
          />
        </div>
      )}
      <div className="mt-0.5 flex flex-1">
        <p
          className={twMerge(
            'text-gray-700 text-sm break-all line-clamp-1',
            selected && 'text-white',
            textClassName
          )}
        >
          {text}
        </p>
      </div>
      <div className="flex items-center gap-1.5">
        {onEdit && (
          <Button
            buttonClassName="bg-transparent h-auto btn-sm btn-icon-xl btn-clear btn-light p-0"
            leftIcon={
              <i className="ki-outline ki-notepad-edit !text-gray-800" />
            }
            disabled={onEditDisabled}
            onClick={onEdit}
          />
        )}
        {onDelete && (
          <Button
            buttonClassName="bg-transparent h-auto btn-sm btn-icon-xl btn-clear btn-light p-0"
            leftIcon={
              <i className="ki-outline ki-cross-square !text-gray-800 hover:!text-red-FF2929" />
            }
            disabled={onDeleteDisabled}
            onClick={onDelete}
          />
        )}
      </div>
      {children && <div className="mt-1 w-full">{children}</div>}
    </div>
  );
};
